/**
 * Weeboholic - Track Order Enhancement
 * Enhances the Shipclues tracking form with modern UX, tab toggling, icons,
 * native direct tracking API rendering, and a beautiful mobile-responsive modal.
 */
(() => {
  'use strict';

  // Global popup handlers required by Shipclues inline onclick="openPopup()" & onclick="closePopup()"
  window.openPopup = function () {
    const popup = document.getElementById('popup');
    if (!popup) return;

    popup.classList.add('active');
    document.body.classList.add('track-modal-open');

    // Scroll to the latest tracking milestone if present
    const lastTrackingEl = document.getElementById('last_tracking');
    const popupContent = document.getElementById('popup-content');
    if (lastTrackingEl && popupContent) {
      setTimeout(() => {
        popupContent.scrollTop = lastTrackingEl.offsetTop - 60;
      }, 100);
    }
  };

  window.closePopup = function () {
    const popup = document.getElementById('popup');
    if (!popup) return;

    popup.classList.remove('active');
    document.body.classList.remove('track-modal-open');
  };

  function setupPopupListeners() {
    const popup = document.getElementById('popup');
    if (!popup) return;

    // Close on backdrop tap / click
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        window.closePopup();
      }
    });

    // Clean up ugly "None" text in transit history items
    popup.querySelectorAll('.event-content p').forEach((p) => {
      const text = p.textContent.trim();
      if (text === 'None' || text === 'null') {
        p.style.display = 'none';
      }
    });
  }

  // Close modal on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      window.closePopup();
    }
  });

  async function loadNativeTrackingData() {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingId = urlParams.get('tracking_id');
    let queryType = urlParams.get('query_type') || 'order_id';
    const input = document.getElementById('shipclues-tracking-input');
    const trackingContainer = document.getElementById('shipclues-tracking');

    if (!trackingId || !trackingContainer || !input) return;

    // Smart AWB detection: If purely numeric and 9+ digits, auto-route to awb_number
    if (queryType === 'order_id' && /^\d{9,}$/.test(trackingId.trim())) {
      queryType = 'awb_number';
      const awbRadio = document.getElementById('awb_number');
      if (awbRadio) awbRadio.checked = true;
    }

    const sellerId = input.dataset.id || '11593';

    // Show native container with modern loading animation
    trackingContainer.style.display = 'block';
    trackingContainer.innerHTML = `
      <div class="track-loading-state">
        <div class="track-spinner"></div>
        <p class="track-loading-title">Fetching Live Courier Status...</p>
        <p class="track-loading-desc">Connecting to carrier systems for <strong>${trackingId}</strong></p>
      </div>
    `;

    try {
      const apiUrl = `https://shipclues.com/tracking-api?seller_id=${sellerId}&${queryType}=${encodeURIComponent(trackingId)}`;
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const rawHtml = await response.text();

      if (!rawHtml || rawHtml.includes('Data not found') || rawHtml.trim() === '') {
        trackingContainer.innerHTML = `
          <div class="track-error-state">
            <div class="track-error-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3 class="track-error-title">No Tracking Information Found</h3>
            <p class="track-error-desc">We couldn't locate active tracking records for <strong>${trackingId}</strong>. If you just placed your order, please allow 12–24 hours for carrier dispatch registration.</p>
          </div>
        `;
        return;
      }

      // Render native HTML into container
      trackingContainer.innerHTML = rawHtml;

      // Enhance the "More Info" button with an icon
      const moreInfoBtn = trackingContainer.querySelector('.more-info');
      if (moreInfoBtn) {
        moreInfoBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>View Transit History</span>
        `;
      }

      // Format status strings (e.g. "in_transit" -> "In Transit")
      trackingContainer.querySelectorAll('.AWB div').forEach((block) => {
        const h3 = block.querySelector('h3');
        const p = block.querySelector('p');
        if (h3 && p && h3.textContent.toLowerCase().includes('status')) {
          const rawStatus = p.textContent.trim().replace(/_/g, ' ');
          p.innerHTML = `<span class="track-status-pill status-${rawStatus.toLowerCase().replace(/\s+/g, '-')}">${rawStatus}</span>`;
        }
      });

      // Format the modal header and close button
      const popup = document.getElementById('popup');
      if (popup) {
        const closeBtn = popup.querySelector('.close');
        if (closeBtn) {
          closeBtn.innerHTML = '&times;';
          closeBtn.setAttribute('aria-label', 'Close transit history');
        }
      }

      // Mark the last milestone so the connecting line stops cleanly
      trackingContainer.querySelectorAll('.timeline').forEach((timeline) => {
        const events = timeline.querySelectorAll('.event');
        if (events.length > 0) {
          events[events.length - 1].classList.add('is-last-event');
        }
      });

      // Setup popup event listeners
      setupPopupListeners();

      // Smooth scroll to tracking results
      setTimeout(() => {
        trackingContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);

    } catch (err) {
      console.warn('Native tracking fetch error, using fallback iframe:', err);
      // Graceful fallback to iframe with generous height
      trackingContainer.innerHTML = `
        <iframe style="width: 100%; min-height: 800px;" src="https://shipclues.com/embedded-tracking-data?seller_id=${sellerId}&${queryType}=${encodeURIComponent(trackingId)}" title="Order Tracking" frameborder="0" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
      `;
    }
  }

  function initTrackOrder() {
    const form = document.getElementById('shipclues-tracking-container');
    const input = document.getElementById('shipclues-tracking-input');
    const trackingContainer = document.getElementById('shipclues-tracking');

    if (!form || !input) return;

    try {
      // 1. Remove inline styles from form and submit button
      form.removeAttribute('style');
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.removeAttribute('style');
        submitBtn.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <span>Track Shipment</span>
        `;
      }

      // 2. Wrap input with search icon container
      if (!input.parentElement.classList.contains('track-input-wrapper')) {
        const inputWrapper = document.createElement('div');
        inputWrapper.className = 'track-input-wrapper';
        input.parentNode.insertBefore(inputWrapper, input);
        inputWrapper.appendChild(input);

        const iconEl = document.createElement('div');
        iconEl.className = 'track-input-icon';
        iconEl.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
        `;
        inputWrapper.insertBefore(iconEl, input);
      }

      // Remove any lingering <br> tags
      form.querySelectorAll('br').forEach((br) => br.remove());

      // 3. Style radio buttons into a segmented toggle
      const radioGroup = form.querySelector('div:first-child');
      const orderIdRadio = document.getElementById('order_id');
      const awbRadio = document.getElementById('awb_number');

      if (radioGroup && orderIdRadio && awbRadio && !radioGroup.classList.contains('track-query-toggle')) {
        radioGroup.className = 'track-query-toggle';
        radioGroup.innerHTML = '';

        // Order ID Option
        const orderOption = document.createElement('label');
        orderOption.className = 'track-query-option';
        orderOption.appendChild(orderIdRadio);
        const orderLabelSpan = document.createElement('span');
        orderLabelSpan.className = 'track-query-label';
        orderLabelSpan.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
          Order ID
        `;
        orderOption.appendChild(orderLabelSpan);
        radioGroup.appendChild(orderOption);

        // AWB Option
        const awbOption = document.createElement('label');
        awbOption.className = 'track-query-option';
        awbOption.appendChild(awbRadio);
        const awbLabelSpan = document.createElement('span');
        awbLabelSpan.className = 'track-query-label';
        awbLabelSpan.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="1" y="3" width="15" height="13"></rect>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
          </svg>
          Tracking / AWB
        `;
        awbOption.appendChild(awbLabelSpan);
        radioGroup.appendChild(awbOption);

        // Update placeholder based on mode
        const updateInputPlaceholder = () => {
          if (orderIdRadio.checked) {
            input.placeholder = 'Enter Order ID (e.g. #WH01234)';
          } else {
            input.placeholder = 'Enter Courier AWB / Tracking ID';
          }
        };

        orderIdRadio.addEventListener('change', updateInputPlaceholder);
        awbRadio.addEventListener('change', updateInputPlaceholder);
        updateInputPlaceholder();
      }

      // 4. Wrap form inside card with header and trust badges
      if (!form.closest('.track-order-card')) {
        const card = document.createElement('div');
        card.className = 'track-order-card';

        // Header
        const header = document.createElement('div');
        header.className = 'track-order-header';
        header.innerHTML = `
          <div class="track-order-badge">
            <span class="track-order-badge-dot"></span>
            LIVE CARRIER TRACKING
          </div>
          <h2 class="track-order-title">Track Your Shipment</h2>
          <p class="track-order-subtitle">Check real-time status and delivery timeline for your Weeboholic order</p>
        `;

        // Trust features
        const features = document.createElement('div');
        features.className = 'track-order-features';
        features.innerHTML = `
          <div class="track-feature-item">
            <div class="track-feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <span class="track-feature-title">Real-Time Sync</span>
            <span class="track-feature-desc">Live status from courier partners</span>
          </div>
          <div class="track-feature-item">
            <div class="track-feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </div>
            <span class="track-feature-title">Pan-India Express</span>
            <span class="track-feature-desc">BlueDart, Delhivery, DTDC & more</span>
          </div>
          <div class="track-feature-item">
            <div class="track-feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </div>
            <span class="track-feature-title">Need Help?</span>
            <span class="track-feature-desc">Support available 10 AM - 7 PM</span>
          </div>
        `;

        // Help bar
        const helpBar = document.createElement('div');
        helpBar.className = 'track-order-help';
        helpBar.innerHTML = `
          <span class="track-order-help-text">Can't find your Order ID? Check your order confirmation <strong>Email</strong> or <strong>WhatsApp message</strong>.</span>
          <a href="/pages/contact" class="track-order-help-link">Contact Support &rarr;</a>
        `;

        form.parentNode.insertBefore(card, form);
        card.appendChild(header);
        card.appendChild(form);
        card.appendChild(features);
        card.appendChild(helpBar);
      }

      // 5. Fetch and render native tracking data directly
      loadNativeTrackingData();

    } catch (err) {
      console.warn('Track order enhancement notice:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTrackOrder);
  } else {
    initTrackOrder();
  }
})();
